import type { MacroRegion, PlaycastMessageMacroRecorderReferenceCaptureBegin } from '@playcastdotio/Messaging';
import type {
  INativeReferenceCaptureClient,
  ReferenceCapturePushResult,
} from '../referenceCapture/NativeReferenceCaptureClient';

type ArmedReply = Awaited<ReturnType<INativeReferenceCaptureClient['begin']>>;
type MarkedReply = Awaited<ReturnType<INativeReferenceCaptureClient['mark']>>;

/**
 * One native reference-capture session for the connected local editor.
 * Live Detection and reference capture both need the capture thread, so `busy`
 * refuses a new session while a detection run is still active.
 */
export class ReferenceCaptureManager {
  private owned?: string;
  /** Playjector never confirmed the end of `owned`; it is ended again before another session may begin. */
  private stale = false;
  private arming = false;

  public constructor(
    private readonly native: INativeReferenceCaptureClient,
    private readonly busy: () => boolean,
    private readonly push: (sessionId: string, result: ReferenceCapturePushResult) => void,
  ) {
    this.native.subscribe((result) => {
      // Playjector ended the session itself (source changed, device lost): ownership is gone, so a new
      // begin must be accepted instead of reporting busy.
      if (result.status === 'failed' && !result.markId && result.sessionId === this.owned) this.forget();
      this.push(result.sessionId, result);
    });
  }

  public async begin(input: PlaycastMessageMacroRecorderReferenceCaptureBegin['message']): Promise<ArmedReply> {
    if (this.busy()) throw new Error('local_host_busy');
    if (this.arming || (this.owned && !this.stale)) throw new Error('reference_capture_busy');
    this.arming = true;
    try {
      if (this.owned) await this.endOwned();
      const armed = await this.native.begin({
        sessionId: input.sessionId, assets: input.assets, outputPrefix: input.outputPrefix,
      });
      if (!this.arming) {
        await this.releaseCancelled(input.sessionId);
        throw new Error('reference_capture_cancelled');
      }
      this.owned = input.sessionId;
      return armed;
    } finally {
      this.arming = false;
    }
  }

  public mark(sessionId: string, markId: string, lookbackMs?: number, region?: MacroRegion): Promise<MarkedReply> {
    if (this.owned !== sessionId || this.stale) return Promise.reject(new Error('reference_capture_session_unknown'));
    return this.native.mark(sessionId, markId, lookbackMs, region);
  }

  public focus(sessionId: string, region: MacroRegion): void {
    if (this.owned !== sessionId || this.stale) return;
    this.native.focus(sessionId, region);
  }

  public async end(sessionId: string): Promise<void> {
    if (this.owned !== sessionId) throw new Error('reference_capture_session_unknown');
    await this.endOwned();
  }

  /** Drop the browser session. An armed capture is ended so the GPU ring is released. */
  public release(): void {
    this.arming = false;
    if (!this.owned) return;
    void this.endOwned().catch(() => undefined);
  }

  /** Ends `owned`; on failure Playjector may still hold it, so ownership is kept (stale) for a retry. */
  private async endOwned(): Promise<void> {
    const sessionId = this.owned;
    if (!sessionId) return;
    try {
      await this.native.end(sessionId);
    } catch (error) {
      if (this.owned === sessionId) this.stale = true;
      throw error;
    }
    if (this.owned === sessionId) this.forget();
  }

  /** `release()` arrived while Playjector was arming: end what it armed, or remember it for the next begin. */
  private async releaseCancelled(sessionId: string): Promise<void> {
    try { await this.native.end(sessionId); }
    catch { this.owned = sessionId; this.stale = true; }
  }

  private forget(): void {
    this.owned = undefined;
    this.stale = false;
  }
}
